import { useState, useEffect, useCallback } from "react"
import { api } from "../utils/apiComponent/apiClient"
import { endpoints } from "../utils/endpoints"

/**
 * Construit une query string à partir d'un objet de paramètres
 */
const buildQuery = (params = {}) => {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, value)
    }
  })
  const str = query.toString()
  return str ? `?${str}` : ""
}

/**
 * Hook générique pour récupérer une ressource de stats
 */
const useStatsResource = (endpoint, params = {}, options = {}) => {
  const { immediate = true } = options
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(immediate && !!endpoint)
  const [error, setError] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const queryString = buildQuery(params)
  const fullUrl = endpoint ? `${endpoint}${queryString}` : null

  const fetchData = useCallback(async () => {
    if (!fullUrl) return null
    setLoading(true)
    setError(null)
    try {
      const response = await api.get(fullUrl)
      setData(response)
      return response
    } catch (err) {
      console.error(`[useStats] Erreur lors de la récupération de ${fullUrl}:`, err)
      setError(err.message || "Erreur lors du chargement des statistiques")
      throw err
    } finally {
      setLoading(false)
    }
  }, [fullUrl])

  useEffect(() => {
    if (immediate && fullUrl) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullUrl, refreshKey])

  const refetch = useCallback(() => {
    setRefreshKey((prev) => prev + 1)
  }, [])

  return { data, loading, error, refetch }
}

/**
 * KPIs globaux pour les cartes du dashboard
 */
export const useStatsOverview = () => {
  return useStatsResource(endpoints.statsOverview)
}

/**
 * Statistiques utilisateurs avec graphiques
 * @param {Object} params - { groupBy, from, to, limit }
 */
export const useStatsUsers = (params = {}) => {
  return useStatsResource(endpoints.statsUsers, params)
}

/**
 * Statistiques cours avec graphiques
 */
export const useStatsCourses = (params = {}) => {
  return useStatsResource(endpoints.statsCourses, params)
}

/**
 * Statistiques inscriptions avec graphiques
 */
export const useStatsEnrollments = (params = {}) => {
  return useStatsResource(endpoints.statsEnrollments, params)
}

/**
 * Statistiques revenus avec graphiques
 */
export const useStatsRevenue = (params = {}) => {
  return useStatsResource(endpoints.statsRevenue, params)
}

/**
 * Statistiques d'engagement avec graphiques
 */
export const useStatsEngagement = (params = {}) => {
  return useStatsResource(endpoints.statsEngagement, params)
}

/**
 * Statistiques webinaires avec graphiques
 */
export const useStatsWebinars = (params = {}) => {
  return useStatsResource(endpoints.statsWebinars, params)
}

/**
 * Statistiques newsletter avec graphiques
 */
export const useStatsNewsletter = (params = {}) => {
  return useStatsResource(endpoints.statsNewsletter, params)
}

/**
 * Tableau de bord complet - combine tous les blocs en une seule requête
 * @param {Object} params - { groupBy, from, to, limit }
 */
export const useStatsDashboard = (params = {}) => {
  return useStatsResource(endpoints.statsDashboard, params)
}

/**
 * Liste paginée des apprenants avec résumé de progression
 * @param {Object} params - { page, limit, search, role, status, sortBy, sortOrder }
 */
export const useLearners = (params = {}) => {
  return useStatsResource(endpoints.statsLearners, params)
}

/**
 * Détails complets d'un apprenant (profil, summary, cours, certificats, paiements, webinaires)
 * @param {string} userId
 */
export const useLearnerDetails = (userId) => {
  return useStatsResource(
    userId ? endpoints.statsLearnerDetails(userId) : null,
    {},
    { immediate: !!userId }
  )
}

/**
 * Progression ultra-détaillée d'un apprenant sur un cours spécifique
 * @param {string} userId
 * @param {string} courseId
 */
export const useLearnerCourseProgress = (userId, courseId) => {
  const enabled = !!userId && !!courseId
  return useStatsResource(
    enabled ? endpoints.statsLearnerCourseProgress(userId, courseId) : null,
    {},
    { immediate: enabled }
  )
}

/**
 * Hook combiné qui retourne toutes les stats utiles pour le dashboard admin
 * Utilise /stats/dashboard pour optimiser les appels
 */
export const useAdminDashboardStats = (params = {}) => {
  const { data, loading, error, refetch } = useStatsDashboard(params)

  return {
    overview: data?.overview || null,
    users: data?.users || null,
    courses: data?.courses || null,
    enrollments: data?.enrollments || null,
    revenue: data?.revenue || null,
    engagement: data?.engagement || null,
    webinars: data?.webinars || null,
    newsletter: data?.newsletter || null,
    generatedAt: data?.generatedAt || null,
    loading,
    error,
    refetch,
  }
}
